/**
 * The idle-behaviour engine.
 *
 * Pokemon in a Grove wander, doze, look around, hop, graze and occasionally
 * turn to face a neighbour. Four constraints shape the whole design:
 *
 *  1. **The player's arrangement is the truth.** A critter wanders *around*
 *     the spot it was placed and drifts back to it, never away from it. If
 *     wandering could relocate a Pokemon, arranging a Grove would be pointless
 *     — and the saved layout would stop matching what anyone sees.
 *
 *  2. **React is not in the loop.** Thirty-six sprites re-rendering at 60fps
 *     is thirty-six reconciliations a frame. Positions are written straight to
 *     the DOM from one rAF loop; React owns *which* sprites exist, and this
 *     owns where they are standing this instant.
 *
 *  3. **Nothing here is game state.** No behaviour grants EXP, and none of it
 *     is sent anywhere. It is decoration, so it can be switched off entirely —
 *     during editing, on a hidden tab, or for someone who asked for less
 *     motion — with no consequence beyond stillness.
 *
 *  4. **Deterministic per Pokemon.** Each critter's rhythm is seeded from its
 *     id, so the same Pokemon has the same temperament every visit rather than
 *     being re-rolled on each page load.
 */

export type Behaviour = 'idle' | 'walk' | 'sleep' | 'look' | 'jump' | 'eat' | 'play';

export interface Critter {
  id: string;
  /** Where the player put it. The anchor everything drifts back toward. */
  homeX: number;
  homeY: number;
  x: number;
  y: number;
  /** Current walk target, in scene percent. */
  targetX: number;
  targetY: number;
  behaviour: Behaviour;
  /** Timestamp (ms) at which the current behaviour expires. */
  until: number;
  /** -1 faces left, 1 faces right. */
  facing: number;
  /** 0..3, a fixed temperament seeded from the id. */
  variant: number;
}

/** How far from home a Pokemon will stray, in scene percent. */
const ROAM_X = 7;
const ROAM_Y = 3.5;
/** Percent of the scene per second. Slow — these are grazing, not commuting. */
const WALK_SPEED = 2.6;
/** Two critters closer than this may notice each other. */
const NOTICE_DISTANCE = 9;

/** Cheap deterministic hash, so a Pokemon's temperament is stable. */
function seedOf(id: string): number {
  let hash = 2166136261;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash);
}

export const idleVariant = (id: string) => seedOf(id) % 4;

/**
 * How long each behaviour lasts, and how likely it is to be chosen next.
 *
 * Weighted so a Grove reads as *calm*: most of the time most things are
 * standing still. Constant motion across three dozen sprites is not liveliness,
 * it is noise, and it makes the scene tiring to look at while you are trying to
 * click one particular Pokemon.
 */
const BEHAVIOURS: Array<{ kind: Behaviour; weight: number; min: number; max: number }> = [
  { kind: 'idle', weight: 34, min: 2200, max: 6000 },
  { kind: 'walk', weight: 22, min: 1600, max: 3600 },
  { kind: 'look', weight: 14, min: 1400, max: 2600 },
  { kind: 'sleep', weight: 10, min: 6000, max: 14_000 },
  { kind: 'eat', weight: 9, min: 2200, max: 4200 },
  { kind: 'jump', weight: 7, min: 900, max: 1500 },
  { kind: 'play', weight: 4, min: 1800, max: 3000 },
];

const TOTAL_WEIGHT = BEHAVIOURS.reduce((sum, row) => sum + row.weight, 0);

function pickBehaviour(): (typeof BEHAVIOURS)[number] {
  let roll = Math.random() * TOTAL_WEIGHT;
  for (const row of BEHAVIOURS) {
    roll -= row.weight;
    if (roll <= 0) return row;
  }
  return BEHAVIOURS[0]!;
}

export function makeCritter(
  id: string,
  x: number,
  y: number,
  flipped: boolean,
): Critter {
  const seed = seedOf(id);
  return {
    id,
    homeX: x,
    homeY: y,
    x,
    y,
    targetX: x,
    targetY: y,
    behaviour: 'idle',
    // Staggered from the seed so a freshly loaded Grove does not have every
    // Pokemon change behaviour on the same frame.
    until: performance.now() + (seed % 4000),
    facing: flipped ? -1 : 1,
    variant: seed % 4,
  };
}

/** Chooses the next behaviour and, for a walk, somewhere to walk to. */
function retarget(critter: Critter, now: number) {
  const choice = pickBehaviour();
  critter.behaviour = choice.kind;
  critter.until = now + choice.min + Math.random() * (choice.max - choice.min);

  if (choice.kind === 'walk') {
    // Always inside the roam box around home, so a Pokemon cannot wander off
    // across the Grove however long it is left running.
    critter.targetX = critter.homeX + (Math.random() * 2 - 1) * ROAM_X;
    critter.targetY = critter.homeY + (Math.random() * 2 - 1) * ROAM_Y;
    critter.facing = critter.targetX < critter.x ? -1 : 1;
  } else {
    critter.targetX = critter.x;
    critter.targetY = critter.y;
    if (choice.kind === 'look') critter.facing = Math.random() < 0.5 ? -1 : 1;
  }
}

/**
 * One simulation step.
 *
 * `bounds` clamps to the walkable ground so nothing strays into the sky or off
 * the edge; it is the same GROUND_TOP the server clamps saved layouts to, so a
 * wandering Pokemon can never stand somewhere a saved one could not.
 */
export function stepCritters(
  critters: Critter[],
  deltaSeconds: number,
  now: number,
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
) {
  for (const critter of critters) {
    if (now >= critter.until) retarget(critter, now);

    if (critter.behaviour === 'walk') {
      const dx = critter.targetX - critter.x;
      const dy = critter.targetY - critter.y;
      const distance = Math.hypot(dx, dy);

      if (distance < 0.3) {
        // Arrived early: stand about rather than jittering on the spot.
        critter.behaviour = 'idle';
        critter.until = now + 1200 + Math.random() * 2400;
      } else {
        const move = Math.min(distance, WALK_SPEED * deltaSeconds);
        critter.x += (dx / distance) * move;
        critter.y += (dy / distance) * move;
      }
    }

    critter.x = Math.max(bounds.minX, Math.min(bounds.maxX, critter.x));
    critter.y = Math.max(bounds.minY, Math.min(bounds.maxY, critter.y));
  }

  // Neighbours notice each other. Checked on a coarse pass rather than every
  // frame by the caller, because it is O(n²) and nobody can see the difference
  // between "turned to look" now and half a second from now.
  return critters;
}

/**
 * Occasionally makes two nearby idle critters turn and play together.
 *
 * Deliberately rare and short. The charm is in noticing it happen once, not in
 * a Grove where everything is permanently interacting.
 */
export function pairInteractions(critters: Critter[], now: number) {
  for (let i = 0; i < critters.length; i++) {
    const a = critters[i]!;
    if (a.behaviour !== 'idle' || now < a.until - 400) continue;

    for (let j = i + 1; j < critters.length; j++) {
      const b = critters[j]!;
      if (b.behaviour !== 'idle') continue;

      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      if (distance > NOTICE_DISTANCE) continue;
      if (Math.random() > 0.012) continue;

      // Turn to face one another, then both play for the same short window.
      a.facing = b.x < a.x ? -1 : 1;
      b.facing = a.x < b.x ? -1 : 1;
      const until = now + 1600 + Math.random() * 1200;
      a.behaviour = 'play';
      b.behaviour = 'play';
      a.until = until;
      b.until = until;
      break;
    }
  }
}

/**
 * Writes positions to the DOM.
 *
 * Depth follows y, so something lower in the scene is nearer the viewer — the
 * same rule the saved layout uses. It has to be recomputed as they walk or a
 * Pokemon strolling in front of a tree would pass behind it.
 */
export function paintCritters(critters: Critter[], elements: Map<string, HTMLElement>) {
  for (const critter of critters) {
    const element = elements.get(critter.id);
    if (!element) continue;
    element.style.left = `${critter.x}%`;
    element.style.top = `${critter.y}%`;
    element.style.zIndex = String(Math.round(critter.y * 10));
    element.dataset.behaviour = critter.behaviour;
    element.dataset.facing = critter.facing < 0 ? 'left' : 'right';
  }
}
